import React from 'react';

import { observer } from 'mobx-react';

import AddIcon from '@mui/icons-material/Add';

import { useStore } from 'utils/hooks/store';

import RuleConditionRow from './RuleConditionRow';
import RuleCombinator from './RuleCombinator';
import { useRuleFieldData } from './useRuleFieldData';
import { newCondition, RuleConditionDraft, RuleDraft, RULE_MAX_CONDITIONS, visibleAttributes } from './registry';
import { AddConditionButton, RuleActions, RuleBuilderRoot, RuleLimitNote } from './styles';

export interface RuleBuilderProps {
  rule: RuleDraft;
  onChange: (rule: RuleDraft) => void;
  submitted: boolean;
}

const RuleBuilder: React.FunctionComponent<RuleBuilderProps> = observer(({ rule, onChange, submitted }) => {
  const { session } = useStore();
  const { optionsByKind, customFieldLabels } = useRuleFieldData();
  const { conditions, combinator } = rule;
  const attributes = visibleAttributes({
    hasObjectManager: Boolean(session?.user?.hasObjectManager),
    hasSites: (session?.user?.organisation?.maximumSitesAllowed ?? 0) > 0,
  });
  const atLimit = conditions.length >= RULE_MAX_CONDITIONS;

  const [rowIds, setRowIds] = React.useState<number[]>(() => conditions.map((_, i) => i));
  const nextIdRef = React.useRef(conditions.length);
  if (rowIds.length !== conditions.length) {
    setRowIds(conditions.map((_, i) => i));
    nextIdRef.current = conditions.length;
  }

  const updateCondition = (index: number, condition: RuleConditionDraft): void => {
    onChange({ ...rule, conditions: conditions.map((c, i) => (i === index ? condition : c)) });
  };

  const removeCondition = (index: number): void => {
    if (conditions.length <= 1) {
      return;
    }
    setRowIds((prev) => prev.filter((_, i) => i !== index));
    onChange({ ...rule, conditions: conditions.filter((_, i) => i !== index) });
  };

  const addCondition = (): void => {
    if (atLimit) {
      return;
    }
    const used = new Set(conditions.map((c) => c.attribute));
    setRowIds((prev) => [...prev, nextIdRef.current]);
    nextIdRef.current += 1;
    onChange({ ...rule, conditions: [...conditions, newCondition(used, attributes)] });
  };

  return (
    <RuleBuilderRoot>
      {conditions.map((condition, index) => (
        <React.Fragment key={rowIds[index]}>
          {index > 0 && (
            <RuleCombinator
              combinator={combinator}
              interactive={index === 1}
              onChange={(next) => onChange({ ...rule, combinator: next })}
            />
          )}
          <RuleConditionRow
            condition={condition}
            rowId={rowIds[index]}
            attributes={attributes}
            customFieldLabels={customFieldLabels}
            optionsByKind={optionsByKind}
            canRemove={conditions.length > 1}
            submitted={submitted}
            onChange={(next) => updateCondition(index, next)}
            onRemove={() => removeCondition(index)}
          />
        </React.Fragment>
      ))}

      <RuleActions>
        {atLimit ? (
          <RuleLimitNote>You can add up to {RULE_MAX_CONDITIONS} conditions.</RuleLimitNote>
        ) : (
          <AddConditionButton type="button" onClick={addCondition}>
            <AddIcon fontSize="small" />
            Add condition
          </AddConditionButton>
        )}
      </RuleActions>
    </RuleBuilderRoot>
  );
});

export default RuleBuilder;
